import { useState, useMemo, useEffect } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import Loader from '../components/utils/Loader';
import { postGiftCard, getGiftCard, deleteGiftCard, getAllGiftCard } from '../components/actions/voucher';

const FALLBACK_IMG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23e5e7eb"/></svg>';

const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const getValueLabel = (denominations = []) => {
  if (!denominations.length) return '-';
  if (denominations[0].value_type === 'Range') {
    const { min_value, max_value } = denominations[0];
    return `₹${min_value} - ₹${max_value}`;
  }
  const values = denominations.map((d) => Number(d.fix_value)).filter((v) => !isNaN(v));
  if (!values.length) return '-';
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? `₹${min}` : `₹${min} - ₹${max}`;
};

export default function GiftCardsPage() {
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [entriesPerPage, setEntriesPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGiftCards, setSelectedGiftCards] = useState([]);
  const [originalSelectedIds, setOriginalSelectedIds] = useState([]);
  const [viewGiftCard, setViewGiftCard] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [lastAddedCount, setLastAddedCount] = useState(0);
  const [lastRemovedCount, setLastRemovedCount] = useState(0);

  // Server se latest selected gift cards fetch karke state sync karta hai
  const refreshSelection = async (clientId, catalog = giftCards) => {
    const res = await getGiftCard(clientId);
    const existingIds = (res?.data || []).map((g) => g.id);
    setOriginalSelectedIds(existingIds);
    setSelectedGiftCards(catalog.filter((c) => existingIds.includes(c.id)));
    return existingIds;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const catalogRes = await getAllGiftCard();
        const catalog = catalogRes?.data || catalogRes || [];
        setGiftCards(catalog);

        const clientId = sessionStorage.getItem('code');
        await refreshSelection(clientId, catalog);
      } catch (err) {
        console.error('Error fetching gift cards:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const isCardSelected = (card) => selectedGiftCards.some((c) => c.id === card.id);

  const filteredGiftCards = useMemo(() => {
    const filtered = giftCards.filter((card) => {
      const matchesSearch =
        (card.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (card.code || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === 'All Status' ||
        (statusFilter === 'Active' && card.status === 1) ||
        (statusFilter === 'Inactive' && card.status !== 1);
      const matchesCategory =
        categoryFilter === 'All Categories' || (card.category || 'Uncategorized') === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });

    // Selected cards ko sabse pehle dikhao — sirf GET API se aaye originalSelectedIds
    // ke hisaab se sort karo, taaki click karte hi (live selection change hone par)
    // list turant reorder na ho. Order sirf refresh/update ke baad badlega.
    const originalSelectedIdSet = new Set(originalSelectedIds);
    return [...filtered].sort((a, b) => {
      const aSelected = originalSelectedIdSet.has(a.id) ? 0 : 1;
      const bSelected = originalSelectedIdSet.has(b.id) ? 0 : 1;
      return aSelected - bSelected;
    });
  }, [giftCards, searchTerm, statusFilter, categoryFilter, originalSelectedIds]);

  const categories = useMemo(
    () => ['All Categories', ...new Set(giftCards.map((c) => c.category || 'Uncategorized'))],
    [giftCards]
  );

  const totalPages = Math.ceil(filteredGiftCards.length / entriesPerPage) || 1;
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedGiftCards = filteredGiftCards.slice(startIndex, startIndex + entriesPerPage);

  const totalGiftCards = giftCards.length;
  const activeGiftCards = giftCards.filter((c) => c.status === 1).length;

  const openView = (card) => {
    setViewGiftCard(card);
    setShowViewModal(true);
  };

  const toggleSelect = (card) => {
    setSelectedGiftCards((prev) =>
      prev.some((c) => c.id === card.id) ? prev.filter((c) => c.id !== card.id) : [...prev, card]
    );
  };

  const resetSelection = () => setSelectedGiftCards(giftCards.filter((c) => originalSelectedIds.includes(c.id)));

  const currentSelectedIds = selectedGiftCards.map((c) => c.id);
  const idsToRemove = originalSelectedIds.filter((id) => !currentSelectedIds.includes(id));
  const cardsToAdd = selectedGiftCards.filter((c) => !originalSelectedIds.includes(c.id));
  const hasChanges = idsToRemove.length > 0 || cardsToAdd.length > 0;

  const handleUpdate = async () => {
    if (!hasChanges) return;
    setUpdating(true);
    try {
      const clientId = sessionStorage.getItem('code');

      if (idsToRemove.length > 0) {
        await deleteGiftCard(clientId, idsToRemove);
      }

      if (cardsToAdd.length > 0) {
        const body = cardsToAdd.map((c) => ({
          id: c.id,
          merchantCode: clientId,
          code: c.code,
          title: c.title,
          giftCardType: c.giftcard_type,
          description: c.description,
          status: c.status,
          imageUrl: c.image,
          createdDate: new Date().toISOString(),
        }));
        await postGiftCard(body);
      }

      // Turant server se latest data le lo taaki UI sync rahe
      await refreshSelection(clientId, giftCards);

      setLastAddedCount(cardsToAdd.length);
      setLastRemovedCount(idsToRemove.length);
      setUpdateStatus('success');
      setStatusMessage('Your merchant gift card lineup is now up to date.');
    } catch (error) {
      setUpdateStatus('error');
      setStatusMessage(`Error updating gift cards: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => (status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800');

  return (
    <DashboardLayout>
      {(loading || updating) && <Loader />}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Gift Card Management</h1>
          <p className="text-gray-600 mt-1">Select a gift card and update it for your customers</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-gift-2-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Gift Cards</p>
                <p className="text-2xl font-bold text-gray-900">{totalGiftCards}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-check-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Gift Cards</p>
                <p className="text-2xl font-bold text-gray-900">{activeGiftCards}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-checkbox-multiple-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Selected</p>
                <p className="text-2xl font-bold text-gray-900">{selectedGiftCards.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="relative w-full lg:w-80">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search gift cards by title or code..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200 text-sm bg-gray-50"
                aria-label="Search gift cards"
              />
            </div>

            <div className="flex gap-3 items-center w-full lg:w-auto">
              <div className="relative w-full sm:w-auto">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer w-full sm:w-auto"
                  aria-label="Filter by status"
                >
                  <option value="All Status">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>

              <div className="relative w-full sm:w-auto">
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer w-full sm:w-auto"
                  aria-label="Filter by category"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer pr-8"
                aria-label="Select entries per page"
              >
                <option value={8}>8</option>
                <option value={16}>16</option>
                <option value={32}>32</option>
                <option value={64}>64</option>
              </select>
              <span className="text-sm text-gray-600">entries</span>
            </div>

            <div className="text-sm text-gray-600">
              Showing {filteredGiftCards.length} results
              {selectedGiftCards.length > 0 && (
                <span className="ml-3 text-blue-600 font-semibold">
                  • {selectedGiftCards.length} selected
                </span>
              )}
            </div>
          </div>
        </div>

        {!loading && filteredGiftCards.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center text-gray-500 border border-gray-100 shadow-sm">
            No gift cards found.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {paginatedGiftCards.map((card) => {
            const isSelected = isCardSelected(card);
            return (
              <div
                key={card.id}
                className={`bg-white rounded-2xl p-4 shadow-sm border-2 transition-all duration-150 hover:shadow-md ${
                  isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <button
                  onClick={() => toggleSelect(card)}
                  aria-pressed={isSelected}
                  aria-label={`Select ${card.title}`}
                  className="w-full text-left"
                >
                  <div className="relative">
                    <div className="w-full aspect-[16/10] rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = FALLBACK_IMG;
                        }}
                      />
                    </div>
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shadow">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                    )}
                  </div>
                  <p className="mt-3 text-sm font-semibold text-gray-900 truncate">{card.title}</p>
                  <p className="text-xs text-gray-500 font-mono truncate">{card.code}</p>
                </button>

                <div className="flex items-center justify-between mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(card.status)}`}>
                    {card.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => openView(card)}
                    className="text-blue-600 hover:text-blue-800"
                    aria-label={`View gift card ${card.title}`}
                  >
                    <i className="ri-eye-line text-lg"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          {hasChanges && (
            <button
              onClick={resetSelection}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
              aria-label="Reset changes"
            >
              Reset changes
            </button>
          )}
          <button
            onClick={handleUpdate}
            disabled={!hasChanges}
            className={`px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
              hasChanges
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            aria-label="Update selected gift cards"
          >
            <i className="ri-refresh-line text-lg"></i>
            Update
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="text-sm text-gray-600">
              Showing {filteredGiftCards.length === 0 ? 0 : startIndex + 1} to{' '}
              {Math.min(startIndex + entriesPerPage, filteredGiftCards.length)} of {filteredGiftCards.length} entries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                Previous
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const pageNum = index + 1 + Math.max(0, currentPage - 3);
                if (pageNum > totalPages) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border rounded text-sm ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    aria-label={`Go to page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {showViewModal && viewGiftCard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Gift Card Details</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close modal"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                <div className="w-full aspect-[16/9] rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                  <img
                    src={viewGiftCard.image}
                    alt={viewGiftCard.title}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = FALLBACK_IMG;
                    }}
                  />
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Title</span>
                      <span className="font-medium">{viewGiftCard.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Code</span>
                      <span className="font-medium font-mono">{viewGiftCard.code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Category</span>
                      <span className="font-medium">{viewGiftCard.category || 'Uncategorized'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Denomination</span>
                      <span className="font-semibold text-blue-600">
                        {getValueLabel(viewGiftCard.denominations)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Quantity</span>
                      <span className="font-medium">{viewGiftCard.max_quantity || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                          viewGiftCard.status
                        )}`}
                      >
                        {viewGiftCard.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {viewGiftCard.description && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-600 line-clamp-6">{stripHtml(viewGiftCard.description)}</p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                    aria-label="Close gift card details"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      toggleSelect(viewGiftCard);
                      setShowViewModal(false);
                    }}
                    className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                      isCardSelected(viewGiftCard)
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                    aria-label={isCardSelected(viewGiftCard) ? 'Deselect this gift card' : 'Select this gift card'}
                  >
                    {isCardSelected(viewGiftCard) ? 'Deselect This Card' : 'Select This Card'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {updateStatus && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
              <div
                className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ring-8 ${
                  updateStatus === 'success' ? 'bg-green-100 ring-green-50' : 'bg-red-100 ring-red-50'
                }`}
              >
                <i
                  className={`text-5xl ${
                    updateStatus === 'success'
                      ? 'ri-checkbox-circle-fill text-green-500'
                      : 'ri-error-warning-fill text-red-500'
                  }`}
                ></i>
              </div>

              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {updateStatus === 'success' ? 'Gift Cards Updated!' : 'Update Failed'}
              </h2>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">{statusMessage}</p>

              {updateStatus === 'success' && (lastAddedCount > 0 || lastRemovedCount > 0) && (
                <div className="flex items-center justify-center gap-3 mb-6">
                  {lastAddedCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 px-3.5 py-1.5 rounded-full text-sm font-semibold">
                      <i className="ri-add-circle-line"></i>
                      {lastAddedCount} added
                    </span>
                  )}
                  {lastRemovedCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 px-3.5 py-1.5 rounded-full text-sm font-semibold">
                      <i className="ri-subtract-line"></i>
                      {lastRemovedCount} removed
                    </span>
                  )}
                </div>
              )}

              <button
                onClick={() => setUpdateStatus(null)}
                className={`w-full py-3 rounded-xl font-semibold text-white shadow-md transition-all hover:shadow-lg active:scale-[0.98] ${
                  updateStatus === 'success'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                    : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                }`}
                aria-label="Close status message"
              >
                {updateStatus === 'success' ? 'Great, Done!' : 'Close'}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}